import React, { memo } from "react";
import { Button } from "@progress/kendo-react-buttons";
import type { ButtonProps } from "@progress/kendo-react-buttons";
import { Spinner, Tooltip, Icon } from "@components/index"
import "./style.css";

export type ButtonVariant = "brand" | "primary" | "default" | "success" | "warning" | "error" | "neutral" | "plum";
export type  IconPosition = "left" | "right";
export type  ButtonSize = "small" | "medium" | "large";

interface DefaultButtonProps extends Omit<ButtonProps, "icon" | "size" | "iconSize"> {
    label?: React.ReactNode;
    icon?: string;
    iconPosition?: IconPosition;
    size?: ButtonSize;
    variant?: ButtonVariant;
    isLoading?: boolean;
    type?: "button" | "submit" | "reset";
    disabled?: boolean;
    className?: string;
    tooltipText?: string;
    tooltipPosition?: "top" | "bottom" | "left" | "right";
    iconSize?: number;
    iconFill?: string;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
    brand: "button-brand",
    primary: "button-primary",
    default: "button-default",
    success: "button-success",
    warning: "button-warning",
    error: "button-error",
    neutral: "button-neutral",
    plum: "button-plum",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
    small: "btn-sm",
    medium: "btn-md",
    large: "btn-lg",
};

const DefaultButton: React.FC<DefaultButtonProps> = memo(({
    label,
    icon,
    iconPosition = "left",
    size = "large",
    variant = "primary",
    isLoading = false,
    type = "button",
    disabled = false,
    className = "",
    tooltipText,
    tooltipPosition = "top",
    iconSize = 12,
    iconFill,
    ...props
}) => {
    const isDisabled = disabled || isLoading;
    const iconProps = isDisabled
        ? { fill: "currentColor", className: "shrink-0" as const }
        : iconFill !== undefined
            ? { fill: iconFill, className: "shrink-0" as const }
            : {
                  className:
                      variant === "default"
                          ? ("fill-gray-black-100" as const)
                          : ("fill-white" as const),
              };
    const classes = [
        "cursor-pointer uppercase focus:shadow flex items-center gap-2",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        isDisabled ? "btn-disabled" : "",
        className
    ].filter(Boolean).join(" ");

    const buttonContent = (
        <>
            {icon && iconPosition === "left" && !isLoading && (
                <Icon name={icon} width={iconSize} height={iconSize} {...iconProps} />
            )}
            {isLoading && (
                <span>
                    <Spinner size="xs" variant={variant === "default" ? "default" : "gray-sand"} />
                </span>
            )}
            {label && label}
            {icon && iconPosition === "right" && !isLoading && (
                <Icon name={icon} width={iconSize} height={iconSize} {...iconProps} />
            )}
        </>
    );
    const button = (
        <Button
            type={type}
            className={classes}
            disabled={isDisabled}
            aria-busy={isLoading}
            {...props}
        >
            {buttonContent}
        </Button>
    );

    if (tooltipText) {
        return (
            <Tooltip position={tooltipPosition}>
                <span tabIndex={-1}>{React.cloneElement(button, { title: tooltipText })}</span>
            </Tooltip>
        );
    }

    return button;
});

export default DefaultButton;
